/**
 * Сверка карты и графа навигации.
 *
 * Пара бессмысленна порознь: координаты в графе заданы в системе
 * `viewBox` карты. Граф от другой карты на глаз не отличить, а маршрут
 * по нему ляжет мимо — сквозь стены и мимо стендов. Поэтому сверка
 * идёт до первого построения, и не сошлось — навигация выключается
 * целиком.
 *
 * Версия сверяется по той же причине: формат может измениться, и молча
 * работать по изменившимся правилам хуже, чем не работать вовсе.
 *
 * Имя карты (`map`) сверяется, только если сервер его прислал. Сейчас
 * он имени не отдаёт — см. docs/map-navigation-backend.md.
 */

export const SUPPORTED_VERSION = '1.0';

/**
 * Достаёт viewBox из корневого <svg>.
 * @param {String} svgText разметка карты
 * @returns {Number[]|null} четыре числа или null
 */
function readViewBox(svgText) {
  if (typeof svgText !== 'string') return null;

  const rootTag = svgText.match(/<svg\b[^>]*>/i);
  if (!rootTag) return null;

  const attr = rootTag[0].match(/viewBox\s*=\s*"([^"]*)"/i);
  if (!attr) return null;

  const numbers = attr[1].trim().split(/[\s,]+/).map(Number);
  if (numbers.length !== 4 || numbers.some((n) => !Number.isFinite(n))) return null;

  return numbers;
}

/**
 * @param {String} svgText разметка карты
 * @param {Object} graph разобранный nav.json
 * @param {String|null} mapName имя файла карты, если сервер его прислал
 * @returns {{ ok: Boolean, problems: String[] }}
 */
export default function checkPair(svgText, graph, mapName = null) {
  const problems = [];

  if (!graph || typeof graph !== 'object') {
    return { ok: false, problems: ['граф не задан или не разобрался'] };
  }

  if (graph.version !== SUPPORTED_VERSION) {
    problems.push(`версия графа ${JSON.stringify(graph.version)}, поддерживается "${SUPPORTED_VERSION}"`);
  }

  const mapBox = readViewBox(svgText);
  const graphBox = Array.isArray(graph.viewBox) ? graph.viewBox : null;

  if (!mapBox) {
    problems.push('у карты не читается viewBox');
  } else if (!graphBox || graphBox.length !== 4) {
    problems.push('в графе нет viewBox');
  } else if (mapBox.some((n, i) => n !== graphBox[i])) {
    problems.push(`viewBox карты [${mapBox}] против графа [${graphBox}]`);
  }

  if (mapName && graph.map && graph.map !== mapName) {
    problems.push(`граф собран для карты "${graph.map}", а открыта "${mapName}"`);
  }

  return { ok: problems.length === 0, problems };
}
